import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useUpdateView } from '@americanexpress/data-manager-utils';
import { useReactlytics } from '@americanexpress/reactlytics';
import styles from '../styles/PageWrapper.scss';
import iconSparkle from '../assets/IconSparkle.svg';
import { DEFAULT_ANALYTICS_PAGE_VIEW } from '../utils/constants';

export const ChatLanding = ({ setInputValue }) => {
  const intl = useIntl();
  const { track } = useReactlytics();
  const updateView = useUpdateView();
  const scrollerRef = useRef(null);
  const visibleRef = useRef(new Set());

  useEffect(() => {
    updateView({
      viewId: `${DEFAULT_ANALYTICS_PAGE_VIEW}Chat`,
    });
  }, [updateView]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-tracking-id');
          if (!id) return;

          const fullyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.98;

          if (fullyVisible && !visibleRef.current.has(id)) {
            visibleRef.current.add(id);

            track({
              event: {
                identifier: 'axp - WishlistBuilder - Chat',
                element: `view-prompt-${id}`,
                type: 'impression',
              },
            });
          }

          if (!fullyVisible) {
            visibleRef.current.delete(id);
          }
        });
      },
      {
        root,
        threshold: [0, 0.98, 1],
      }
    );

    root.querySelectorAll('[data-tracking-id]').forEach((tile) => {
      observer.observe(tile);
    });

    // eslint-disable-next-line consistent-return -- disconnect observer
    return () => observer.disconnect();
  }, [track]);
  const exampleInputs = [
    {
      id: 'defaultInput1',
      text: intl.formatMessage({ id: 'userInput.example1' }),
    },
    {
      id: 'defaultInput2',
      text: intl.formatMessage({ id: 'userInput.example2' }),
    },
    {
      id: 'defaultInput3',
      text: intl.formatMessage({ id: 'userInput.example3' }),
    },
  ];

  const handlePromptSelection = (id, text) => {
    track({
      event: {
        identifier: 'axp - WishlistBuilder - Chat',
        element: `select-prompt-${id}`,
        type: 'click',
      },
    });
    setInputValue(text);
  };

  return (
    <>
      <div className={`${styles.titleMarginTop} flex flex-column flex-item-grow`}>
        <div className={styles.chatIntroHeaderWrapper}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.2 } }}
            className="heading-serif-large-regular flex flex-column"
            style={{ fontSize: '34px !important', fontWeight: '300' }}
          >
            {intl.formatMessage({ id: 'chatIntroTitle1' })}
            {intl.formatMessage({ id: 'chatIntroTitle2' })}
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.7 } }}
            className={`${styles.subTitleContainer} flex flex-column body-3 pad-2-t`}
          >
            {intl.formatMessage({ id: 'chatIntroSubtitle1' })}
            {intl.formatMessage({ id: 'chatIntroSubtitle2' })}
          </motion.div>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className={`${styles.scrollbarHide} ${styles.reverseMargin} flex pad-1-b scroll-x`}
      >
        {exampleInputs.map((item) => (
          <button
            key={item.id}
            data-tracking-id={item.id}
            type="button"
            aria-label={`Example input for AI suggestions - ${item.text}`}
            onClick={() => handlePromptSelection(item.id, item.text)}
            className={
                `${styles.hoverUnderline} 
                ${styles.borderRadiusText}
                ${styles.buttonBox}
                margin-1-b pad-2-lr pad-2-t pad-3-b`
              }
          >
            <div className="flex flex-align-center">
              <img
                src={iconSparkle}
                alt="Decorative icon for AI suggestions"
              />
            </div>
            <div
              className={`text-align-left body-3 ${styles.exampleInputText}`}
            >
              {item.text}
            </div>
          </button>
        )
        )}
      </div>
    </>
  );
};

ChatLanding.propTypes = {
  setInputValue: PropTypes.func.isRequired,
};
